// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

export type CategoryId =
  | 'tags'
  | 'vs-smuggling'
  | 'zero-width'
  | 'bidi'
  | 'soft-hyphen'
  | 'whitespace'
  | 'line-separator'
  | 'control'
  | 'pua'
  | 'invisible-filler'
  | 'deprecated'
  | 'interlinear'
  | 'noncharacter'
  | 'homoglyph'
  | 'combining'
  | 'typographic'
  | 'invalid-byte';

/**
 * Severity is a property of the OCCURRENCE, not of the code point.
 *
 * The same U+200D is `expected` between two emoji and `suspicious` in the middle of a Latin
 * identifier. A rule that cannot look at the neighbours is a classification, not a detection.
 *
 *  · payload    — a decoded hidden message. The headline; there is nothing above this.
 *  · suspicious — invisible or deceptive, with no plausible legitimate reason in this context.
 *  · notable    — real, but routine or context-dependent: a BOM, an NBSP in prose, a Nerd Font
 *                 glyph, a curly quote. Worth seeing; not worth alarm.
 *  · expected   — a legitimate use charsight positively identified: an emoji ZWJ sequence, Persian
 *                 ZWNJ, a registered ideographic variation sequence, a balanced isolate pair.
 *                 NEVER cleaned, and shown only when you ask to see everything.
 */
export type Severity = 'payload' | 'suspicious' | 'notable' | 'expected';

/**
 * The tier a category sits in. **The axis is CONSEQUENCE, not threat** — and that is what keeps the
 * honesty gate structural rather than editorial. It gives a narrow no-break space and a curly quote
 * somewhere to live that carries no accusation about who typed them, while still explaining exactly
 * why they broke your CSV.
 *
 *  1 · carries a message      — tag characters, variation-selector chains. It decodes to something.
 *  2 · changes what you see   — bidi controls, invisible letters, private use, controls.
 *  3 · breaks your tools      — the space family, line separators, soft hyphens.
 *  4 · looks like something else — mixed script and lookalikes. Visible, and wrong.
 *  5 · formatting             — curly quotes, dashes, ellipses. No colour, no badge, no severity.
 */
export type Tier = 1 | 2 | 3 | 4 | 5;

export const TIER_LABELS: Record<Tier, string> = {
  1: 'Carries a message',
  2: 'Changes what you see',
  3: 'Breaks your tools',
  4: 'Looks like something else',
  5: 'Formatting',
};

/** The unrankable form, for `findings.csv`. See the note on FINDINGS_HEADER for why. */
export const TIER_SLUGS: Record<Tier, string> = {
  1: 'carries-a-message',
  2: 'changes-what-you-see',
  3: 'breaks-your-tools',
  4: 'looks-like-something-else',
  5: 'formatting',
};

export type Action = 'remove' | 'replace' | 'keep';

/** What kind of file is this? The same character means different things in prose and in a Makefile. */
export type FileKind = 'code' | 'data' | 'prose' | 'markup' | 'terminal' | 'unknown';

export interface Finding {
  category: CategoryId;
  severity: Severity;
  /** The code point, or -1 for an invalid byte or a multi-code-point finding. */
  cp: number;
  /** Byte offset of the start of this finding, within the original buffer. */
  byteStart: number;
  /** Byte offset one past the end. */
  byteEnd: number;
  line: number;
  column: number;
  /** How many code points this finding spans. 1 unless it is a run. */
  runLength: number;
  /** Why this is here, in one sentence a human can act on. */
  reason: string;
  /** For a run that decodes to something: the decoded message. */
  decoded?: string;
  /** Hex of the decoded bytes, for a variation-selector payload that is not text. */
  decodedHex?: string;
  action: Action;
  /** Replacement text when action is 'replace'. */
  replacement?: string;
}

export interface ScanContext {
  fileKind: FileKind;
  /** Short script aliases that make up the bulk of the document. */
  dominantScripts: ReadonlySet<string>;
  /**
   * Byte length of the byte-order mark the decoder consumed, 0 when there is none.
   *
   * The BOM is not a unit — it is encoding metadata, not text — so the scanner would never see it
   * otherwise. It still deserves a finding: a BOM breaks shebang lines, several JSON parsers and
   * CSV header matching, and a user staring at a file that "starts with #!/bin/sh" and will not run
   * has come to exactly the right tool.
   */
  bomLength?: number;
}

export interface ScanResult {
  findings: Finding[];
  context: ScanContext;
  /** Total code points scanned, for the "N of M characters" line. */
  codePoints: number;
  invalidBytes: number;
}
